import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip,
  CircularProgress, Select, MenuItem, FormControl, InputLabel, Autocomplete,
  useMediaQuery, useTheme
} from '@mui/material';
import { Send, Refresh, CheckCircle, TouchApp } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';
import axios from 'axios';

const VL_COLORS = [
  { light: '#fff8e1', dark: '#f9a825' }, { light: '#e3f2fd', dark: '#1976d2' },
  { light: '#e8f5e9', dark: '#388e3c' }, { light: '#f3e5f5', dark: '#7b1fa2' },
  { light: '#eceff1', dark: '#546e7a' }
];

const vlColorCache = {};
let colorIndex = 2;

const getVLColors = (vlName) => {
  if (!vlName) return { light: '#f5f5f5', dark: '#9e9e9e' };
  const name = vlName.toLowerCase().trim();
  if (name === 'openers' || name === 'op') return VL_COLORS[0];
  if (name === 'op+null' || name === 'opnull' || name.includes('op+null')) return VL_COLORS[1];
  if (vlColorCache[name]) return vlColorCache[name];
  const colors = VL_COLORS[colorIndex % VL_COLORS.length];
  vlColorCache[name] = colors;
  colorIndex++;
  return colors;
};

function DemMaildome() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(addDays(new Date(), 7));
  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [offerUrls, setOfferUrls] = useState([]);
  const [sendForm, setSendForm] = useState({ subject: '', sender_name: '', preheader: '', creative_id: '', category_id: '', source_id: '', limits: '', offer_url_id: '' });
  const [emailItems, setEmailItems] = useState({ senders: [], subjects: [], preheaders: [] });
  const [sending, setSending] = useState(false);
  const [categoryFromCampaign, setCategoryFromCampaign] = useState(false);

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchPendingCampaigns(); }, [startDate, endDate]);

  const fetchCategories = async () => {
    try { const res = await axios.get('/admin/dem/maildome/categories'); setCategories(res.data.data || []); }
    catch (error) { console.error('Error fetching categories:', error); }
  };

  const fetchPendingCampaigns = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/dem/maildome/pending', { params: { start_date: format(startDate, 'yyyy-MM-dd'), end_date: format(endDate, 'yyyy-MM-dd') } });
      setPendingCampaigns(res.data.data || []);
    } catch (error) { console.error('Error:', error); setMessage({ type: 'error', text: 'Failed to load pending campaigns' }); }
    finally { setLoading(false); }
  };

   const handleOpenSendDialog = async (entry) => {
    setSelectedEntry(entry);
    setSendForm({ subject: '', sender_name: '', preheader: '', creative_id: '', category_id: '', source_id: '', limits: '', offer_url_id: '' });
    setEmailItems({ senders: [], subjects: [], preheaders: [] });
    setCategoryFromCampaign(false);
    try { const res = await axios.get(`/admin/dem/maildome/creatives/${entry.campaign_id}`); setCreatives(res.data.data || []); }
    catch (error) { console.error('Error fetching creatives:', error); setCreatives([]); }
    try { const res = await axios.get(`/admin/campaigns/${entry.campaign_id}/offer-urls`); setOfferUrls(res.data.data || []); }
    catch (error) { console.error('Error fetching offer URLs:', error); setOfferUrls([]); }
    try { const res = await axios.get(`/admin/campaigns/${entry.campaign_id}/email-items`); setEmailItems(res.data.data || { senders: [], subjects: [], preheaders: [] }); }
    catch (error) { console.error('Error fetching email items:', error); }
    try { 
      const res = await axios.get(`/admin/campaigns/${entry.campaign_id}`); 
      if (res.data?.category_id) {
        setSendForm(prev => ({ ...prev, category_id: res.data.category_id }));
        setCategoryFromCampaign(true);
      }
    } catch (error) { console.error('Error fetching campaign:', error); }
    setSendDialogOpen(true);
  };

  const handleSendToMaildome = async () => {
    if (!sendForm.subject || !sendForm.sender_name || !sendForm.category_id || !sendForm.creative_id) {
      setMessage({ type: 'error', text: 'Please fill all required fields' }); return;
    }
    setSending(true);
    try {
      const res = await axios.post('/admin/dem/maildome/send', {
        calendar_id: selectedEntry.id, subject: sendForm.subject, sender_name: sendForm.sender_name,
        preheader: sendForm.preheader || '', creative_id: sendForm.creative_id, category_id: sendForm.category_id,
        source_id: sendForm.source_id || '', limits: sendForm.limits ? parseInt(sendForm.limits) : null,
        offer_url_id: sendForm.offer_url_id || null
      });
      setSendDialogOpen(false);
      setMessage({ type: 'success', text: `"${selectedEntry.campaign_name}" sent! (ID: ${res.data.data?.cid || 'N/A'})` });
      fetchPendingCampaigns();
    } catch (error) { setSendDialogOpen(false); setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to send' }); }
    finally { setSending(false); }
  };

  const handleMarkAsManual = async (entry) => {
    try {
      await axios.post(`/admin/dem/maildome/manual/${entry.id}`);
      setMessage({ type: 'success', text: `"${entry.campaign_name}" marked as manually planned` });
      fetchPendingCampaigns();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to mark as manual' });
    }
  };


  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = dateStr.substring ? dateStr.substring(0, 10) : dateStr;
    return `${d.substring(8, 10)}/${d.substring(5, 7)}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Maildome</Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <DatePicker label="From" value={startDate} onChange={(newDate) => setStartDate(newDate)} slotProps={{ textField: { size: 'small', sx: { width: { xs: 110, sm: 140 } } } }} />
            <DatePicker label="To" value={endDate} onChange={(newDate) => setEndDate(newDate)} slotProps={{ textField: { size: 'small', sx: { width: { xs: 110, sm: 140 } } } }} />
            <IconButton onClick={fetchPendingCampaigns} size="small"><Refresh sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        </Box>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

        <Card>
          <CardContent sx={{ py: 1.5, px: { xs: 1, sm: 2 } }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
              Pending ({pendingCampaigns.length})
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer sx={{ maxHeight: { xs: 400, sm: 600 } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 55, p: 0.5 }}>Date</TableCell>
                      {!isMobile && <TableCell sx={{ width: 45, p: 0.5 }}>Time</TableCell>}
                      <TableCell sx={{ p: 0.5 }}>DB</TableCell>
                      <TableCell sx={{ p: 0.5 }}>Campaign</TableCell>
                      {!isTablet && <TableCell sx={{ width: 80, p: 0.5 }}>VL</TableCell>}
                      {!isTablet && <TableCell sx={{ width: 90, p: 0.5 }}>Domain</TableCell>}
                      <TableCell align="center" sx={{ width: isMobile ? 80 : 150, p: 0.5 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingCampaigns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                          <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>All campaigns sent!</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pendingCampaigns.map((entry) => {
                        const colors = getVLColors(entry.virtual_list_name);
                        const canSend = entry.maildome_db_id && entry.maildome_vl_ids;
                        return (
                          <TableRow key={entry.id} hover>
                            <TableCell sx={{ p: 0.5, fontSize: '0.7rem' }}>{formatDate(entry.send_date)}</TableCell>
                            {!isMobile && <TableCell sx={{ p: 0.5, fontSize: '0.7rem' }}>{entry.send_time?.substring(0, 5) || '-'}</TableCell>}
                            <TableCell sx={{ p: 0.5 }}>
                              <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, maxWidth: { xs: 60, sm: 100 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.database_name}</Typography>
                              {!entry.maildome_db_id && <Typography variant="caption" color="error" sx={{ fontSize: '0.6rem', display: 'block' }}>No DB ID</Typography>}
                            </TableCell>
                            <TableCell sx={{ p: 0.5 }}>
                              <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, maxWidth: { xs: 80, sm: 150 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.campaign_name}</Typography>
                            </TableCell>
                            {!isTablet && (
                              <TableCell sx={{ p: 0.5 }}>
                                <Chip label={entry.virtual_list_name || '-'} size="small" sx={{ bgcolor: colors.light, fontSize: '0.6rem', height: 18 }} />
                                {!entry.maildome_vl_ids && <Typography variant="caption" color="error" sx={{ fontSize: '0.55rem', display: 'block' }}>No VL IDs</Typography>}
                              </TableCell>
                            )}
                            {!isTablet && <TableCell sx={{ p: 0.5, fontSize: '0.65rem' }}>{entry.sender_domain || '-'}</TableCell>}
                            <TableCell align="center" sx={{ p: 0.5 }}>
                              <Box display="flex" gap={0.5} justifyContent="center">
                                {isMobile ? (
                                  <>
                                    <IconButton size="small" color="primary" onClick={() => handleOpenSendDialog(entry)} disabled={!canSend}><Send sx={{ fontSize: 18 }} /></IconButton>
                                    <IconButton size="small" color="warning" onClick={() => handleMarkAsManual(entry)}><TouchApp sx={{ fontSize: 18 }} /></IconButton>
                                  </>
                                ) : (
                                  <>
                                    <Button size="small" variant="contained" startIcon={<Send sx={{ fontSize: 14 }} />} onClick={() => handleOpenSendDialog(entry)} disabled={!canSend} sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}>Send</Button>
                                    <Button size="small" variant="outlined" color="warning" startIcon={<TouchApp sx={{ fontSize: 14 }} />} onClick={() => handleMarkAsManual(entry)} sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}>Manual</Button>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Send to Maildome Dialog */}
        <Dialog open={sendDialogOpen} onClose={() => !sending && setSendDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Send to Maildome</DialogTitle>
          <DialogContent>
            {selectedEntry && (
              <Box>
                <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1, mb: 2, mt: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>DB:</strong> {selectedEntry.database_name} (ID: {selectedEntry.maildome_db_id})</Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Campaign:</strong> {selectedEntry.campaign_name}</Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>VL:</strong> {selectedEntry.virtual_list_name} (IDs: {selectedEntry.maildome_vl_ids})</Typography>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}><strong>Scheduled:</strong> {formatDate(selectedEntry.send_date)} @ {selectedEntry.send_time?.substring(0, 5)}</Typography>
                </Box>

                 {(emailItems.senders.length > 0 || emailItems.subjects.length > 0 || emailItems.preheaders.length > 0) && (
                  <Alert severity="info" sx={{ mb: 2 }}>Select from saved items or type manually</Alert>
                )}

                <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
                   <Autocomplete
                    freeSolo
                    size={isMobile ? 'small' : 'medium'}
                    options={emailItems.senders.map(s => s.value)}
                    value={sendForm.sender_name}
                    onChange={(e, v) => setSendForm({ ...sendForm, sender_name: v || '' })}
                    onInputChange={(e, v) => setSendForm({ ...sendForm, sender_name: v || '' })}
                    renderInput={(params) => <TextField {...params} required label="Sender Name" placeholder="e.g., Support Team" />}
                    slotProps={{ popper: { sx: { zIndex: 1600 } } }}
                  />
                  <Autocomplete
                    freeSolo
                    size={isMobile ? 'small' : 'medium'}
                    options={emailItems.subjects.map(s => s.value)}
                    value={sendForm.subject}
                    onChange={(e, v) => setSendForm({ ...sendForm, subject: v || '' })}
                    onInputChange={(e, v) => setSendForm({ ...sendForm, subject: v || '' })}
                    renderInput={(params) => <TextField {...params} required label="Subject" placeholder="Email subject" />}
                    slotProps={{ popper: { sx: { zIndex: 1600 } } }}
                  />
                  <Autocomplete
                    freeSolo
                    size={isMobile ? 'small' : 'medium'}
                    options={emailItems.preheaders.map(s => s.value)}
                    value={sendForm.preheader}
                    onChange={(e, v) => setSendForm({ ...sendForm, preheader: v || '' })}
                    onInputChange={(e, v) => setSendForm({ ...sendForm, preheader: v || '' })}
                    renderInput={(params) => <TextField {...params} label="Preheader" placeholder="Preview text" />}
                    slotProps={{ popper: { sx: { zIndex: 1600 } } }}
                  />
                   {categoryFromCampaign ? (
                    <TextField 
                      size={isMobile ? 'small' : 'medium'} 
                      fullWidth 
                      label="Category" 
                      value={categories.find(c => c.id === parseInt(sendForm.category_id))?.name || ''} 
                      disabled 
                      helperText="From Campaign Settings" 
                    />
                  ) : (
                    <Autocomplete size={isMobile ? 'small' : 'medium'} options={categories} getOptionLabel={(option) => option.name} value={categories.find(c => c.id === parseInt(sendForm.category_id)) || null} onChange={(e, v) => setSendForm({ ...sendForm, category_id: v?.id || '' })} renderInput={(params) => <TextField {...params} required label="Category" />} slotProps={{ popper: { sx: { zIndex: 1600 } } }} />
                  )}
                  <FormControl fullWidth required size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Creative</InputLabel>
                    <Select value={sendForm.creative_id} onChange={(e) => setSendForm({ ...sendForm, creative_id: e.target.value })} label="Creative" MenuProps={{ sx: { zIndex: 1600 } }}>
                      {creatives.length === 0 ? <MenuItem disabled>No creatives</MenuItem> : creatives.map(c => <MenuItem key={c.creative_id} value={c.creative_id}>{c.name} (ID: {c.creative_id})</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Offer URL</InputLabel>
                    <Select value={sendForm.offer_url_id} onChange={(e) => setSendForm({ ...sendForm, offer_url_id: e.target.value })} label="Offer URL" MenuProps={{ sx: { zIndex: 1600 } }}>
                      <MenuItem value="">Default URL</MenuItem>
                      {offerUrls.map(u => <MenuItem key={u.offer_url_id} value={u.offer_url_id}>{u.name}{u.is_default ? ' (default)' : ''}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField fullWidth label="Source ID" value={sendForm.source_id} onChange={(e) => setSendForm({ ...sendForm, source_id: e.target.value })} placeholder="e.g., INVIO1" size={isMobile ? 'small' : 'medium'} />
                  <TextField fullWidth label="Limits" type="number" value={sendForm.limits} onChange={(e) => setSendForm({ ...sendForm, limits: e.target.value })} placeholder="Max recipients" size={isMobile ? 'small' : 'medium'} />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>Cancel</Button>
            <Button variant="contained" onClick={handleSendToMaildome} disabled={sending} startIcon={sending ? <CircularProgress size={16} /> : <Send />} size={isMobile ? 'small' : 'medium'}>
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default DemMaildome;
